import { useLayoutEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import { tokens } from "../../styles/Theme/Theme";
import { NAV_ITEMS, isHiddenOn, descriptionId } from "./navItems";

const SCROLLED = "[data-scrolled='true'] &";

// Warm "ignite" gradient - decoration only (the current-page underline).
const igniteGradient = "linear-gradient(90deg, #FFD93B, #FFA63C, #FF5E62)";

// A hovered label turns one solid warm colour, not the gradient: the amber and
// coral stops measured under 2:1 behind the pill, and gradient-clipped text
// cannot be guaranteed a contrast ratio or survive forced-colours mode.
const hoverText = tokens.colors.accentLight.lemon;

const reducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Magnetic transition shared by every interactive item
const magneticMotion = {
  transition: `color ${tokens.transitions.fast}, transform 200ms cubic-bezier(0.2, 0.8, 0.3, 1.2)`,
  "@media (prefers-reduced-motion: reduce)": { transition: `color ${tokens.transitions.fast}` },
};

const focusRing = {
  "&:focus-visible": {
    outline: `2px solid ${tokens.colors.brand.blue}`,
    outlineOffset: "2px",
  },
};

// Label styles - warms to lemon on hover. The current page keeps a short
// gradient underline, because nothing used to tell a visitor which page they
// were on.
const linkStyles = {
  fontFamily: tokens.typography.fontFamily.heading,
  fontSize: tokens.typography.fontSize.sm,
  fontWeight: tokens.typography.fontWeight.medium,
  letterSpacing: "0.5px",
  color: tokens.colors.text.primary,
  textDecoration: "none",
  padding: "8px 12px",
  borderRadius: tokens.borderRadius.md,
  position: "relative",
  ...magneticMotion,
  "&:hover": { color: hoverText },
  "&::after": {
    content: '""',
    position: "absolute",
    left: "12px",
    right: "12px",
    bottom: "3px",
    height: "2px",
    borderRadius: "2px",
    background: igniteGradient,
    transform: "scaleX(0)",
    transition: `transform ${tokens.transitions.normal}`,
  },
  "&[aria-current='page']::after": { transform: "scaleX(1)" },
  ...focusRing,
};

// The paw: the site's home link. Hidden on the homepage itself (you are already
// there), and on every other page it wiggles when pointed at or focused while a
// small "Home" label drops in under it - an interviewer once could not tell what
// the paw meant.
const pawLabelShown = { opacity: 1, transform: "translate(-50%, 0)" };

const pawStyles = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  width: 36,
  height: 36,
  borderRadius: "50%",
  color: tokens.colors.text.primary,
  ...magneticMotion,
  "&:hover": {
    backgroundColor: tokens.colors.background.glass,
    color: tokens.colors.brand.mint,
  },
  "& svg": { fontSize: 20, transformOrigin: "50% 80%" },
  // The label hangs just below the pill, on the pill's own dark scrim, so it
  // reads over the white TopCurve and the gradient alike. It is decoration for
  // sighted pointer users: the link's accessible name is already "Home".
  "& .paw-label": {
    position: "absolute",
    top: "calc(100% + 10px)",
    left: "50%",
    transform: "translate(-50%, -4px)",
    padding: "3px 10px",
    borderRadius: "999px",
    background: "rgba(18, 18, 18, 0.78)",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    color: "#fff",
    fontFamily: tokens.typography.fontFamily.heading,
    fontSize: "12px",
    fontWeight: tokens.typography.fontWeight.medium,
    letterSpacing: "0.5px",
    lineHeight: 1.4,
    whiteSpace: "nowrap",
    pointerEvents: "none",
    opacity: 0,
    transition: "opacity 160ms ease, transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1)",
  },
  "&:hover .paw-label, &:focus-visible .paw-label": pawLabelShown,
  "@media (prefers-reduced-motion: reduce)": {
    transition: `color ${tokens.transitions.fast}`,
    "& .paw-label": { transform: "translate(-50%, 0)", transition: "opacity 160ms ease" },
  },
  "@keyframes pawWiggle": {
    "0%": { rotate: "0deg" },
    "25%": { rotate: "-14deg" },
    "55%": { rotate: "10deg" },
    "80%": { rotate: "-6deg" },
    "100%": { rotate: "0deg" },
  },
  // `rotate`, not `transform`: the magnetic pull writes transform on the link,
  // and the wiggle lives on the icon, so neither can cancel the other.
  "@media (prefers-reduced-motion: no-preference)": {
    "&:hover svg, &:focus-visible svg": { animation: "pawWiggle 500ms ease-out" },
  },
  "&:focus-visible": {
    outline: `2px solid ${tokens.colors.brand.blue}`,
    outlineOffset: "-2px",
  },
};

const NavbarDesktop = () => {
  const { pathname } = useLocation();
  const groupRef = useRef(null);

  const hoverBorder = "rgba(255, 255, 255, 0.28)";
  const hoverShadow = "0 8px 32px rgba(0, 0, 0, 0.14)";

  // The paw slot collapses on "/" and grows back elsewhere, which resizes the
  // pill smoothly instead of snapping. It clips its content only while closed
  // or mid-animation, so at rest the magnetic lean and focus ring are not cut.
  const pawHidden = isHiddenOn(NAV_ITEMS[0], pathname);
  const [pawAnimating, setPawAnimating] = useState(false);
  const firstPawRender = useRef(true);
  useLayoutEffect(() => {
    if (firstPawRender.current) {
      firstPawRender.current = false;
      return undefined;
    }
    setPawAnimating(true);
    // A timer rather than transitionend: with reduced motion there is no
    // transition, and no event would ever come to unclip it.
    const timer = setTimeout(() => setPawAnimating(false), 380);
    return () => clearTimeout(timer);
  }, [pawHidden]);

  // --- Spotlight glow tracks the cursor; nav items lean magnetically toward it.
  // One update per frame at most, however fast mousemove fires.
  const frame = useRef(0);
  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      const el = groupRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      el.style.setProperty("--spot-x", `${clientX - rect.left}px`);
      el.style.setProperty("--spot-y", `${clientY - rect.top}px`);

      // Respect reduced-motion: glow stays, magnetic movement is skipped
      if (reducedMotion()) return;

      el.querySelectorAll("[data-magnetic]").forEach((item) => {
        const r = item.getBoundingClientRect();
        const dx = clientX - (r.left + r.width / 2);
        const dy = clientY - (r.top + r.height / 2);
        const dist = Math.hypot(dx, dy);
        const max = 90; // pull radius in px
        if (dist < max) {
          const pull = (1 - dist / max) * 0.35;
          item.style.transform = `translate(${dx * pull}px, ${dy * pull}px)`;
        } else {
          item.style.transform = "";
        }
      });
    });
  };

  const handleMouseLeave = () => {
    cancelAnimationFrame(frame.current);
    frame.current = 0;
    const el = groupRef.current;
    if (!el) return;
    el.style.setProperty("--spot-x", "-200px"); // park the glow offscreen
    el.querySelectorAll("[data-magnetic]").forEach((item) => {
      item.style.transform = "";
    });
  };

  return (
    <Box
      sx={{
        display: { xs: "none", lg: "block" },
        position: "relative",
        pointerEvents: "auto",
        transition: `transform ${tokens.transitions.normal}`,
        // Recentred in the compact bar once scrolled (132px resting -> 68px bar).
        [SCROLLED]: { transform: "translateY(-32px)" },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      <Box
        ref={groupRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          position: "relative",
          px: 1,
          py: 0.5,
          borderRadius: "999px",
          // Permanent dark scrim - the labels' contrast depends on it, whatever
          // is scrolled underneath, so hover deepens it and never lightens it.
          // It used to swap to white glass on hover, which over the compact
          // bar's white glass on a light page left every label near 1:1.
          // No backdrop blur of its own: once scrolled it sits on the nav's
          // blurred bar (a blur of a blur).
          background: "rgba(18, 18, 18, 0.6)",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          transition: `background ${tokens.transitions.normal}, border-color ${tokens.transitions.normal}, box-shadow ${tokens.transitions.normal}`,
          // Keep every interactive item above the spotlight glow
          "& > *:not(.nav-spotlight)": {
            position: "relative",
            zIndex: 1,
          },
          "&:hover": {
            background: "rgba(18, 18, 18, 0.72)",
            borderColor: hoverBorder,
            boxShadow: hoverShadow,
          },
          "&:hover .nav-spotlight": { opacity: 1 },
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        {/* Cursor-tracking spotlight glow (sits behind the items) */}
        <Box
          className="nav-spotlight"
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "999px",
            pointerEvents: "none",
            zIndex: 0,
            opacity: 0,
            transition: `opacity ${tokens.transitions.normal}`,
            background:
              "radial-gradient(150px circle at var(--spot-x, -200px) var(--spot-y, 50%), rgba(231, 60, 126, 0.30), rgba(35, 166, 213, 0.14) 45%, transparent 72%)",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        />

        {NAV_ITEMS.map((item) => {
          const shared = {
            component: NavLink,
            to: item.to,
            end: item.to === "/",
            "data-magnetic": true,
            "aria-describedby": descriptionId(item.key),
          };

          if (item.Icon) {
            const { Icon } = item;
            return (
              <Box
                key={item.key}
                sx={{
                  display: "grid",
                  gridTemplateColumns: pawHidden ? "0fr" : "1fr",
                  opacity: pawHidden ? 0 : 1,
                  // Cancel the flex gap too, so the pill closes up fully.
                  mr: pawHidden ? -1 : 0,
                  transition:
                    "grid-template-columns 320ms cubic-bezier(0.2, 0.8, 0.2, 1), margin 320ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 200ms ease",
                  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                }}
                inert={pawHidden}
              >
                <Box sx={{ minWidth: 0, overflow: pawHidden || pawAnimating ? "hidden" : "visible" }}>
                  <Box {...shared} aria-label={item.label} sx={pawStyles}>
                    <Icon />
                    <span className="paw-label" aria-hidden="true">
                      {item.label}
                    </span>
                  </Box>
                </Box>
              </Box>
            );
          }

          return (
            <Box key={item.key} {...shared} sx={linkStyles}>
              {item.label}
            </Box>
          );
        })}
      </Box>

      {/* What each link leads to, as its accessible description - the line the
          mobile menu shows under each label. */}
      {NAV_ITEMS.map((item) => (
        <span key={item.key} id={descriptionId(item.key)} hidden>
          {item.description}
        </span>
      ))}
    </Box>
  );
};

export default NavbarDesktop;
